import { type SessionResponse, type WorkOSUser } from '@reach/shared-types/workos-auth';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { partnerApi } from '../lib/api';
import { getAuthorizationUrl } from '../lib/workos';
import { forgetActiveGeneration } from '../pages/feature-setup/automations-tab/reputation-defaults-menu/activeGenerationStorage';
import { clearAiSegmentDraft } from '../pages/feature-setup/segments-tab/describe-segment-modal/types';
import { AuthContextType, AuthState } from '../types/auth';
import { AuthContext } from './AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

interface AuthProviderProps {
  children: ReactNode;
}

const UNAUTHENTICATED_STATE: AuthState = {
  user: null,
  organization: null,
  role: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    organization: null,
    role: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const revalidationInFlightRef = useRef(false);

  const checkExistingSession = useCallback(async (options?: { isMount?: boolean }): Promise<void> => {
    const isMount = options?.isMount ?? false;
    try {
      // Backend will read the httpOnly cookie automatically
      const response: SessionResponse = await partnerApi.validateSession();
      const { authenticated, user, organization } = response;
      // Role will be added to the response schema
      const role = (response as SessionResponse & { role?: string }).role;

      if (authenticated && user) {
        // Set cross-domain cookie so the marketing site can swap CTAs for returning users
        if (window.location.hostname.endsWith('.embedreach.com')) {
          document.cookie =
            'active_reach_user=1; Domain=.embedreach.com; Path=/; Max-Age=2592000; Secure; SameSite=Lax';
        }

        setAuthState({
          user: user as WorkOSUser,
          organization,
          role: role || 'member',
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        setAuthState(UNAUTHENTICATED_STATE);
      }
    } catch (error) {
      console.error('Session check failed:', error);
      if (isMount) {
        setAuthState({ ...UNAUTHENTICATED_STATE, error: 'Session validation failed' });
      }
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    void checkExistingSession({ isMount: true });
  }, [checkExistingSession]);

  useEffect(() => {
    const runRevalidation = async () => {
      if (revalidationInFlightRef.current) return;
      revalidationInFlightRef.current = true;
      try {
        await checkExistingSession({ isMount: false });
      } finally {
        revalidationInFlightRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      void runRevalidation();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      void runRevalidation();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [checkExistingSession]);

  const login = async (organizationId?: string) => {
    try {
      const authUrl = await getAuthorizationUrl(organizationId);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login failed:', error);
      setAuthState(prev => ({
        ...prev,
        error: 'Failed to initiate login',
      }));
      throw error; // Re-throw to handle in the UI
    }
  };

  const logout = () => {
    // sessionStorage outlives the redirect below, so per-user scratch state has
    // to be dropped here or the next user in this tab inherits it.
    clearAiSegmentDraft();
    forgetActiveGeneration();

    // Clear local auth state
    setAuthState({
      user: null,
      organization: null,
      role: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });

    // Redirect to logout endpoint which will clear the cookie and redirect to root
    // Backend call is needed to clear the http-only cookie
    window.location.href = `${API_BASE_URL}/api/auth/logout`;
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    setAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
